import { ROUTES, type Route } from './router'

/**
 * Où mène une notification.
 *
 * La colonne `route` d'une notification est une simple chaîne, et elle vient de
 * la base — donc, un jour, d'un serveur. **On ne suit jamais ce qu'elle dit
 * sans l'avoir reconnu.** C'est le rôle de ce module : traduire une chaîne
 * arbitraire en une cible parmi trois, ou en rien du tout.
 *
 * Une chaîne plutôt que deux colonnes `target_entity` / `target_id` : ça évite
 * une migration des deux côtés, et le protocole de synchronisation n'a rien à
 * apprendre. Le prix est ce parseur, qui doit rester une liste BLANCHE — comme
 * `Sync::Registry` côté serveur, et pour la même raison.
 */
export type NotificationTarget =
  /** Un onglet, et rien de plus. */
  | { kind: 'route'; route: Route }
  /** Les journées prévues d'une date, à confirmer. */
  | { kind: 'day'; day: string }
  /** Une opération précise, ouverte en détail. */
  | { kind: 'transaction'; id: string }

/** `2026-09-08` — la forme d'une date, et rien d'autre. */
const DAY = /^\d{4}-\d{2}-\d{2}$/
/** Un UUID, sans présumer de sa version : les anciennes lignes sont en v4. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Traduit la colonne `route` en cible, ou renvoie `null` si rien n'est
 * reconnu — auquel cas la notification se contente d'être lue.
 *
 * Le paramètre est lu à la main plutôt qu'avec `URL` : construire une URL
 * absolue à partir d'une chaîne venue d'ailleurs, c'est accepter
 * `https://ailleurs.example` comme destination.
 */
export function parseTarget(route: string | null | undefined): NotificationTarget | null {
  if (!route) return null

  const [path, query] = route.split('?', 2)
  if (!(ROUTES as readonly string[]).includes(path)) return null

  const params = new URLSearchParams(query ?? '')

  const day = params.get('day')
  if (day) return DAY.test(day) ? { kind: 'day', day } : null

  const transaction = params.get('transaction')
  if (transaction) {
    return UUID.test(transaction) ? { kind: 'transaction', id: transaction } : null
  }

  return { kind: 'route', route: path as Route }
}

/** La forme inverse, pour que les règles n'écrivent pas la chaîne à la main. */
export function dayTarget(day: string): string {
  return `/transactions?day=${day}`
}

export function transactionTarget(id: string): string {
  return `/transactions?transaction=${id}`
}
